import { ChatBubbleBottomCenterTextIcon } from "@heroicons/react/24/outline"
import { ResponsiveLine } from "@nivo/line"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { FC, useEffect, useState } from "react"
import { LabValue } from "../../types/LabReport"
import { classNames } from "../../utils/classNames"
import {
  FilterConfig,
  TestValueChartData,
  filterData,
  getFilterOptions,
  testValuesToChartData,
} from "../../utils/data"
import { DebouncedInput } from "../common/DebouncedInput"
import { Filters, VariableFilter } from "../common/Filters/Filters"
import Modal from "../common/Modal"
import { fuzzyFilter } from "../common/Tables/utils"
import { TrendBadge } from "../results/TrendBadge"
import { formatNotes, getResultValueColor } from "../results/utils"

interface Props {
  labValues: LabValue[]
}

const columnHelper = createColumnHelper<LabValue>()
const columns = [
  columnHelper.accessor("name", {
    header: "Test",
    cell: (info) => <span className="block max-w-[15ch] truncate lg:max-w-[20ch]">{info.getValue()}</span>,
  }),
  columnHelper.accessor("latestResult", {
    header: "Result",
    cell: (info) => {
      const row = info.row.original
      const min = row.range?.[0] ?? row.min
      const max = row.range?.[1] ?? row.max
      const isNumericValue = !isNaN(parseFloat(row.latestResult))

      return (
        <span
          className={classNames(
            "relative block max-w-[30ch] truncate",
            getResultValueColor({ ...row, result: row.latestResult }),
          )}>
          {min !== undefined && isNumericValue && <span className="text-gray-400">{min} - </span>}
          {info.getValue()}
          {max !== undefined && isNumericValue && <span className="text-gray-400"> - {max}</span>}
        </span>
      )
    },
  }),
  columnHelper.accessor("valueTrend", {
    header: "Trend",
    cell: (props: any) => {
      let row = props.row.original
      const valueTrend = props.getValue()

      return valueTrend ? (
        <>
          {row.values.length > 1 ? (
            <div className="relative">
              <button
                onClick={() => {
                  props.setChartData({
                    name: row.name,
                    data: testValuesToChartData(row.values, row.min, row.max),
                    min: row.min,
                    max: row.max,
                  })
                }}
                className="transition-opacity hover:opacity-80">
                <TrendBadge {...valueTrend} />
              </button>
            </div>
          ) : (
            <div>
              <TrendBadge {...valueTrend} />
            </div>
          )}
        </>
      ) : null
    },
  }),
  columnHelper.display({
    id: "latestResultAt",
    header: "Updated at",
    cell: (props: any) => {
      return (
        <span>
          {new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "2-digit",
            year: "2-digit",
          }).format(new Date(props.row.original.latestResultAt))}
        </span>
      )
    },
  }),
  columnHelper.display({
    id: "actions",
    cell: (props: any) => {
      const row = props.row.original
      return (
        <span className="flex items-center justify-end gap-x-2.5">
          {row.notes || row.name === "Comments" ? (
            <button onClick={() => props.setNotes(row.notes || row.values[0].result)}>
              <ChatBubbleBottomCenterTextIcon className="h-5 w-5 text-gray-400 hover:text-gray-700" />
            </button>
          ) : null}
        </span>
      )
    },
  }),
]

export const PatientLabValuesTable: FC<Props> = ({ labValues }) => {
  // Table Data
  const [filters, setFilters] = useState<VariableFilter[]>([])
  const filterConfigs: FilterConfig[] = [
    { key: "name", title: "Name" },
    { key: "code", title: "Code" },
  ]
  const filterOptions = getFilterOptions(labValues, filterConfigs)
  const [filteredData, setFilteredData] = useState(labValues)
  useEffect(() => {
    setFilteredData(filterData(labValues, filters))
  }, [labValues, filters])

  const [search, setSearch] = useState("")
  const table = useReactTable({
    data: filteredData,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter: search,
    },
    onGlobalFilterChange: setSearch,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  const [notes, setNotes] = useState<string>()

  const [chartData, setChartData] = useState<{
    name: string
    data: TestValueChartData
    min?: number
    max?: number
  }>()

  return (
    <div className="card">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <h2 className="text-lg font-semibold text-gray-900 lg:shrink-0">Lab Values</h2>
        <div className="flex items-stretch justify-between">
          <div className="flex items-center gap-x-2">
            <div>
              <label htmlFor="search" className="sr-only">
                Search lab values
              </label>
              <DebouncedInput
                name="search"
                type="search"
                className="input mt-0 h-auto py-1.5"
                placeholder="Search"
                value={search}
                onChange={(value) => setSearch(value)}
              />
            </div>
            <Filters
              viewName="labValues"
              buttonClassName="text-sm py-1.5"
              filters={filters}
              setFilters={setFilters}
              filterOptions={filterOptions}
            />
          </div>
        </div>
      </div>
      {labValues.length === 0 ? (
        <div className="mt-4 text-gray-600">No lab values found.</div>
      ) : (
        <>
          <div className="mt-4 flow-root overflow-hidden">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header, i) => (
                          <th
                            scope="col"
                            className={classNames(
                              "relative py-2 text-left text-sm font-semibold text-gray-900",
                              i === 0
                                ? "pr-5 pl-3 sm:pl-0"
                                : i === headerGroup.headers.length - 1
                                  ? "pr-3 pl-2 sm:pr-0"
                                  : "px-2",
                            )}
                            key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {table.getRowModel().rows.length > 0 ? (
                      table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell, i) => (
                            <td
                              className={classNames(
                                "font-regular py-2 text-sm whitespace-nowrap text-gray-500",
                                i === 0
                                  ? "pr-5 pl-3 sm:pl-0"
                                  : i === row.getVisibleCells().length - 1
                                    ? "pr-3 pl-2 sm:pr-0"
                                    : "px-2",
                              )}
                              key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, {
                                ...cell.getContext(),
                                setNotes,
                                setChartData,
                              })}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          className="font-regular px-4 py-4 text-center text-sm whitespace-nowrap text-gray-500"
                          colSpan={table.getHeaderGroups()?.[0]?.headers?.length || 100}>
                          No lab values found for the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      <Modal
        open={!!notes}
        setOpen={(open) => {
          if (!open) {
            setNotes(undefined)
          }
        }}>
        <div>
          <div className="font-medium text-gray-900">Notes</div>
          <p className="mt-3 w-[65ch] whitespace-pre-wrap text-gray-600">{formatNotes(notes ?? "")}</p>
        </div>
      </Modal>

      <Modal
        open={!!chartData}
        setOpen={(open) => {
          if (!open) {
            setChartData(undefined)
          }
        }}>
        <div className="h-[400px] w-[350px] md:h-[450px] md:w-[700px] lg:h-[500px] lg:w-[800px]">
          <div className="font-medium text-gray-900">{chartData?.name}</div>
          <ResponsiveLine
            animate
            colors={["rgb(180, 180, 180)"]}
            curve="monotoneX"
            data={chartData?.data ?? []}
            margin={{
              bottom: 50,
              left: 30,
              right: 20,
              top: 30,
            }}
            useMesh
            xScale={{
              type: "time",
              precision: "minute",
              format: "time:%b %dT%H:%M",
            }}
            xFormat={"time:%b %d %H:%m"}
            axisBottom={{
              legendPosition: "middle",
              format: "%b %d %H:%M",
            }}
            yScale={{
              type: "linear",
              min: "auto",
              max: "auto",
              stacked: false,
              reverse: false,
            }}
            pointColor={({ point }) => {
              return point.data.isAbnormal ? "rgb(239, 68, 68)" : "rgb(34, 197, 94)"
            }}
            // Show min/max lines on chart
            markers={[
              ...(chartData?.min !== undefined
                ? [
                    {
                      axis: "y" as const,
                      value: chartData.min,
                      lineStyle: { stroke: "rgb(34, 197, 94)", strokeWidth: 2, strokeDasharray: "5,5" },
                      legendPosition: "top-left" as const,
                    },
                  ]
                : []),
              ...(chartData?.max !== undefined
                ? [
                    {
                      axis: "y" as const,
                      value: chartData.max,
                      lineStyle: { stroke: "rgb(34, 197, 94)", strokeWidth: 2, strokeDasharray: "5,5" },
                      legendPosition: "top-left" as const,
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </Modal>
    </div>
  )
}
